import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { adminApi, customerApi } from './lib/api';
import { queryClient } from './lib/queryClient';

// Homepage
import Homepage from './pages/HomepageCampaign';

// Blog
import Blog from './pages/Blog';
import BlogPost from './pages/blog/BlogPost';

// SEO Landing Pages
import NightClubJamshedpur from './pages/seo/NightClubJamshedpur';
import DJNightJamshedpur from './pages/seo/DJNightJamshedpur';
import BirthdayPartyVenueJamshedpur from './pages/seo/BirthdayPartyVenueJamshedpur';
import VIPTableBookingJamshedpur from './pages/seo/VIPTableBookingJamshedpur';
import NewYearPartyJamshedpur from './pages/seo/NewYearPartyJamshedpur';
import BarJamshedpur from './pages/seo/BarJamshedpur';
import LadiesNightJamshedpur from './pages/seo/LadiesNightJamshedpur';
import PrivatePartyVenueJamshedpur from './pages/seo/PrivatePartyVenueJamshedpur';

// Contact Page
import ContactPage from './pages/ContactPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';

// Customer pages
import CheckInPage from './pages/customer/CheckInPage';
import OTPTestPage from './pages/customer/OTPTestPage';
import PaymentPage from './pages/customer/PaymentPage';
import PaymentStatusPage from './pages/customer/PaymentStatusPage';
import SuccessPage from './pages/customer/SuccessPage';
import FailurePage from './pages/customer/FailurePage';
import PendingPage from './pages/customer/PendingPage';
import PublicTicketPage from './pages/customer/PublicTicketPage';
import ShortLinkResolver from './pages/ShortLinkResolver';

// V2 Event Engine — public + checkout
import EventsListPage from './pages/EventsListPage';
import EventDetailPage from './pages/EventDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import BookingPaymentStatusPage from './pages/customer/BookingPaymentStatusPage';
import NazakatShararatPage from './pages/customer/NazakatShararatPage';
import NazakatTicketPage from './pages/customer/NazakatTicketPage';

// Account (customer auth + dashboard)
import AccountLoginPage from './pages/account/LoginPage';
import PromoterLoginPage from './pages/account/PromoterLoginPage';
import AccountSignupPage from './pages/account/SignupPage';
import AccountDashboard from './pages/account/DashboardPage';
import PromoterDashboardPage from './pages/account/PromoterPage';
import PromoterManagerPage from './pages/admin/PromoterManagerPage';
import PromotersLandingPage from './pages/PromotersLandingPage';
import PromoterApplyPage from './pages/PromoterApplyPage';
import AccountEvents from './pages/account/EventsPage';
import AccountBookings from './pages/account/BookingsPage';
import AccountProfile from './pages/account/ProfilePage';
import AccountWallet from './pages/account/WalletPage';

// Admin pages
import AdminLoginPage from './pages/admin/LoginPage';
import AdminDashboard from './pages/admin/DashboardPage';
import AdminAnalyticsDashboard from './pages/admin/AnalyticsDashboardPage';
import AdminPassScan from './pages/admin/PassScanPage';
import AdminCheckIns from './pages/admin/CheckInsPage';
import AdminCheckInDetail from './pages/admin/CheckInDetailPage';
import AdminPointsScan from './pages/admin/PointsScanPage';
import AdminPointsManualRedeem from './pages/admin/PointsManualRedeemPage';
import AdminPointsAnalytics from './pages/admin/PointsAnalyticsPage';
import AdminPointsIssue from './pages/admin/PointsIssuePage';
import AdminPayments from './pages/admin/PaymentsPage';
import AdminGateCodes from './pages/admin/GateCodesPage';
import AdminSpecialEvent from './pages/admin/SpecialEventAdminPage';
import AdminSpecialEventBookings from './pages/admin/SpecialEventBookingsPage';
import AdminSpecialEventScan from './pages/admin/SpecialEventScanPage';
import AdminPricingRules from './pages/admin/PricingRulesPage';
import AdminUsers from './pages/admin/UsersPage';
import AdminReports from './pages/admin/ReportsPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import BackupsPage from './pages/admin/BackupsPage';
import ServerUsagePage from './pages/admin/ServerUsagePage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import AdminCustomers from './pages/admin/CustomersPage';
import AdminCustomerDetail from './pages/admin/CustomerDetailPage';
import AdminEvents from './pages/admin/EventsPage';
import AdminEventEditor from './pages/admin/EventEditorPage';
import AdminBookings from './pages/admin/BookingsPage';
import AdminBookingDetail from './pages/admin/BookingDetailPage';
// Instagram Import feature removed (2026-06-04) — no longer in use.
import AdminDraftEdit from './pages/admin/DraftEditPage';
import AdminMarketingDashboard from './pages/admin/marketing/MarketingDashboardPage';
import AdminTemplates from './pages/admin/marketing/TemplatesPage';
import AdminTemplateEditor from './pages/admin/marketing/TemplateEditorPage';
import AdminCampaigns from './pages/admin/marketing/CampaignsPage';
import AdminCampaignWizard from './pages/admin/marketing/CampaignWizardPage';
import AdminCampaignDetail from './pages/admin/marketing/CampaignDetailPage';
import AdminWhatsAppPage from './pages/admin/marketing/WhatsAppPage';
import AdminWhatsAppWizard from './pages/admin/marketing/WhatsAppCampaignWizardPage';
import AdminWhatsAppDetail from './pages/admin/marketing/WhatsAppCampaignDetailPage';
import AdminSmsHealth from './pages/admin/marketing/SmsHealthPage';
import { MarketingRoute } from './components/admin/MarketingRoute';
import { MarketingErrorBoundary } from './components/admin/MarketingErrorBoundary';
import { canAccessPath, landingPathForRole } from './lib/rolePermissions';
import { useRefCaptureOnNavigate } from './lib/refTracking';

// ── Admin Auth Context ────────────────────────────────────────────────────────
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { const u = localStorage.getItem('su_user'); return u ? JSON.parse(u) : null; }
    catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('su_token');
    if (token) {
      adminApi.getMe()
        .then(res => { setUser(res.data); localStorage.setItem('su_user', JSON.stringify(res.data)); })
        .catch((err) => {
          // Only clear the session on a DEFINITIVE auth failure (401 / 403).
          // Network errors, 5xx, CORS hiccups, timeouts during pod restarts
          // must NOT log the user out — that caused the 2-minute logout bug
          // when the backend container restarted briefly.
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            localStorage.removeItem('su_token');
            localStorage.removeItem('su_user');
            setUser(null);
          }
          // Otherwise, keep the cached user; next API call will re-validate.
        })
        .finally(() => setLoading(false));
    } else { setLoading(false); }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('su_token', token);
    localStorage.setItem('su_user', JSON.stringify(userData));
    setUser(userData);
  };
  const logout = () => {
    localStorage.removeItem('su_token'); localStorage.removeItem('su_user'); setUser(null);
  };
  return <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>;
}

// ── Customer Auth Context ─────────────────────────────────────────────────────
const CustomerAuthContext = createContext(null);
export const useCustomerAuth = () => useContext(CustomerAuthContext);

function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(() => {
    try { const c = localStorage.getItem('su_customer'); return c ? JSON.parse(c) : null; }
    catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('su_customer_token');
    if (token) {
      customerApi.me()
        .then(res => { setCustomer(res.data); localStorage.setItem('su_customer', JSON.stringify(res.data)); })
        .catch((err) => {
          // Only clear the session on a definitive 401 / 403. Transient
          // network errors / 5xx must NOT log the customer out (the bug that
          // caused 2-minute logouts after backend pod restarts).
          const status = err?.response?.status;
          if (status === 401 || status === 403) {
            localStorage.removeItem('su_customer_token');
            localStorage.removeItem('su_customer');
            setCustomer(null);
          }
        })
        .finally(() => setLoading(false));
    } else { setLoading(false); }
  }, []);

  const loginCustomer = (token, data) => {
    localStorage.setItem('su_customer_token', token);
    localStorage.setItem('su_customer', JSON.stringify(data));
    setCustomer(data);
  };
  const logoutCustomer = () => {
    localStorage.removeItem('su_customer_token'); localStorage.removeItem('su_customer'); setCustomer(null);
  };
  const updateCustomerData = (data) => {
    setCustomer(data); localStorage.setItem('su_customer', JSON.stringify(data));
  };
  return (
    <CustomerAuthContext.Provider value={{ customer, loading, loginCustomer, logoutCustomer, updateCustomerData }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

// ── Route guards ──────────────────────────────────────────────────────────────
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/admin/login" state={{ from: location }} replace />;
  // Block roles from visiting pages they don't have access to — bounce to
  // their landing page rather than 403'ing.
  if (!canAccessPath(user.role, location.pathname)) {
    return <Navigate to={landingPathForRole(user.role)} replace />;
  }
  return children;
}

function CustomerRoute({ children }) {
  const { customer, loading } = useCustomerAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!customer) {
    // Route promoter-area requests to the WhatsApp-OTP-only login, not the
    // shared password login. This way prospective promoters never hit a
    // password screen during onboarding.
    const isPromoterPath = location.pathname.startsWith('/account/promoter');
    const loginPath = isPromoterPath ? '/account/promoter/login' : '/account/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }
  return children;
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0A0A' }}>
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-[#B9B9B9] text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Role-aware redirect for `/admin` index — bounces each role to a sensible
// landing page (e.g. gate_scan → /admin/scan-pass).
function AdminRoleIndex() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/admin/login" replace />;
  return <Navigate to={landingPathForRole(user.role)} replace />;
}

// Global referral capture — listens for `?ref=` on every navigation and
// posts to the backend so the httpOnly cookie gets set + URL is cleaned.
function RefCaptureMount() {
  useRefCaptureOnNavigate();
  return null;
}

import WhatsAppFloat from './components/common/WhatsAppFloat';
import { initAnalytics, notifyRoute } from './lib/analytics';

// Mount inside <BrowserRouter> so useLocation() can drive route-change pings.
function AnalyticsMount() {
  const location = useLocation();
  React.useEffect(() => {
    initAnalytics({ backendUrl: process.env.REACT_APP_BACKEND_URL || '' });
  }, []);
  React.useEffect(() => {
    notifyRoute();
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <CustomerAuthProvider>
        <BrowserRouter>
          <RefCaptureMount />
          <AnalyticsMount />
          <WhatsAppFloat />
          <Routes>
            {/* Homepage */}
            <Route path="/" element={<Homepage />} />
            
            {/* Blog */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            
            {/* SEO Landing Pages */}
            <Route path="/night-club-in-jamshedpur" element={<NightClubJamshedpur />} />
            <Route path="/dj-night-jamshedpur" element={<DJNightJamshedpur />} />
            <Route path="/birthday-party-venue-jamshedpur" element={<BirthdayPartyVenueJamshedpur />} />
            <Route path="/vip-table-booking-jamshedpur" element={<VIPTableBookingJamshedpur />} />
            <Route path="/new-year-party-jamshedpur" element={<NewYearPartyJamshedpur />} />
            <Route path="/bar-in-jamshedpur" element={<BarJamshedpur />} />
            <Route path="/ladies-night-jamshedpur" element={<LadiesNightJamshedpur />} />
            <Route path="/private-party-venue-jamshedpur" element={<PrivatePartyVenueJamshedpur />} />

            {/* Contact Page */}
            <Route path="/contact" element={<ContactPage />} />
            
            {/* Privacy Policy */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faq" element={<FAQPage />} />
            
            {/* Guest check-in flow */}
            <Route path="/checkin" element={<CheckInPage />} />
            <Route path="/otp-test" element={<OTPTestPage />} />
            <Route path="/checkin/pay" element={<PaymentPage />} />
            <Route path="/checkin/payment-status" element={<PaymentStatusPage />} />
            <Route path="/checkin/success" element={<SuccessPage />} />
            <Route path="/checkin/failure" element={<FailurePage />} />
            <Route path="/checkin/pending" element={<PendingPage />} />

            {/* Public token-based ticket (no auth, standalone) */}
            <Route path="/tickets/:token" element={<PublicTicketPage />} />
            <Route path="/t/:code" element={<ShortLinkResolver />} />

            {/* V2 Event Engine — public listing & detail (login-less browsing) */}
            <Route path="/events" element={<EventsListPage />} />
            {/* Promoter onboarding (P2) */}
            <Route path="/promoters"       element={<PromotersLandingPage />} />
            <Route path="/promoters/apply" element={<PromoterApplyPage />} />
            <Route path="/events/:slug" element={<EventDetailPage />} />
            <Route path="/events/:slug/checkout" element={<CheckoutPage />} />
            <Route path="/events/specialevents/nazakat-shararat" element={<NazakatShararatPage />} />
            <Route path="/events/specialevents/nazakat-shararat/ticket/:token" element={<NazakatTicketPage />} />
            <Route path="/checkout/payment-status" element={<BookingPaymentStatusPage />} />

            {/* Customer account area */}
            <Route path="/account/login" element={<AccountLoginPage />} />
            <Route path="/account/promoter/login" element={<PromoterLoginPage />} />
            <Route path="/account/signup" element={<AccountSignupPage />} />
            <Route path="/account" element={<CustomerRoute><AccountDashboard /></CustomerRoute>} />
            <Route path="/account/events" element={<CustomerRoute><AccountEvents /></CustomerRoute>} />
            <Route path="/account/bookings" element={<CustomerRoute><AccountBookings /></CustomerRoute>} />
            <Route path="/account/profile" element={<CustomerRoute><AccountProfile /></CustomerRoute>} />
            <Route path="/account/wallet" element={<CustomerRoute><AccountWallet /></CustomerRoute>} />
            <Route path="/account/promoter" element={<CustomerRoute><PromoterDashboardPage /></CustomerRoute>} />

            {/* Admin area */}
            <Route path="/admin" element={<AdminRoleIndex />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminRoute><AdminAnalyticsDashboard /></AdminRoute>} />
            <Route path="/admin/operations" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/scan-pass" element={<AdminRoute><AdminPassScan /></AdminRoute>} />
            <Route path="/admin/checkins" element={<AdminRoute><AdminCheckIns /></AdminRoute>} />
            <Route path="/admin/checkins/:id" element={<AdminRoute><AdminCheckInDetail /></AdminRoute>} />
            <Route path="/admin/payments" element={<AdminRoute><AdminPayments /></AdminRoute>} />
            <Route path="/admin/gate-codes" element={<AdminRoute><AdminGateCodes /></AdminRoute>} />
            <Route path="/admin/special-events" element={<AdminRoute><AdminSpecialEvent /></AdminRoute>} />
            <Route path="/admin/special-events/bookings" element={<AdminRoute><AdminSpecialEventBookings /></AdminRoute>} />
            <Route path="/admin/special-events/scan" element={<AdminRoute><AdminSpecialEventScan /></AdminRoute>} />
            <Route path="/admin/pricing-rules" element={<AdminRoute><AdminPricingRules /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/admin/promoters" element={<AdminRoute><PromoterManagerPage /></AdminRoute>} />
            <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
            <Route path="/admin/analytics" element={<AdminRoute><AnalyticsPage /></AdminRoute>} />
            <Route path="/admin/backups" element={<AdminRoute><BackupsPage /></AdminRoute>} />
            <Route path="/admin/server" element={<AdminRoute><ServerUsagePage /></AdminRoute>} />
            <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
            <Route path="/admin/customers/:id" element={<AdminRoute><AdminCustomerDetail /></AdminRoute>} />
            <Route path="/admin/events" element={<AdminRoute><AdminEvents /></AdminRoute>} />
            <Route path="/admin/events/new" element={<AdminRoute><AdminEventEditor /></AdminRoute>} />
            <Route path="/admin/events/:id/edit" element={<AdminRoute><AdminEventEditor /></AdminRoute>} />
            <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
            <Route path="/admin/bookings/:id" element={<AdminRoute><AdminBookingDetail /></AdminRoute>} />
            <Route path="/admin/points/scan" element={<AdminRoute><AdminPointsScan /></AdminRoute>} />
            <Route path="/admin/points/manual-redeem" element={<AdminRoute><AdminPointsManualRedeem /></AdminRoute>} />
            <Route path="/admin/points/issue" element={<AdminRoute><AdminPointsIssue /></AdminRoute>} />
            <Route path="/admin/points/analytics" element={<AdminRoute><AdminPointsAnalytics /></AdminRoute>} />
            <Route path="/admin/events/:id/edit-draft" element={<AdminRoute><AdminDraftEdit /></AdminRoute>} />
            <Route path="/admin/marketing" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminMarketingDashboard /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/templates" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminTemplates /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/templates/new" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminTemplateEditor /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/templates/:id/edit" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminTemplateEditor /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/campaigns" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminCampaigns /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/campaigns/new" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminCampaignWizard /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/campaigns/:id" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminCampaignDetail /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/whatsapp" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminWhatsAppPage /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/whatsapp/new" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminWhatsAppWizard /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/whatsapp/:id" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminWhatsAppDetail /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />
            <Route path="/admin/marketing/sms-health" element={<AdminRoute><MarketingRoute><MarketingErrorBoundary><AdminSmsHealth /></MarketingErrorBoundary></MarketingRoute></AdminRoute>} />

            <Route path="*" element={<Navigate to="/checkin" replace />} />
          </Routes>
          <Toaster richColors position="top-right" theme="dark" />
        </BrowserRouter>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default function AppWithHelmet() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </HelmetProvider>
  );
}
